import { createAsyncThunk } from "@reduxjs/toolkit";
import { alertVariants } from "@visiblethread/react-components";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal } from "slices/app/actions/modals";
import { formatDateArray } from "utils";

import localhost from "../../apis/localhost";
import { addToJobQueue, pageLoading, removeFromJobQueue, showGlobalAlert } from "../app/appSlice";
import { removeProposalById, setCategories, setGateMode } from "./proposalsSlice";

export const mapProposals = (data) => {
  return data.map((column) => ({
    ...column,
    proposals: column.items.map((proposal) => ({
      id: proposal.id,
      opportunityId: proposal.opportunityId,
      name: proposal.title,
      agency: proposal.departmentAgency,
      dueDate:
        formatDateArray(proposal.responseDeadline) ||
        formatDateArray(proposal.dueDate),
      startedBy: proposal.startedBy,
      estimatedValue: proposal.estimatedValue,
      assignees: proposal.assignees || [],
    })),
  }));
};

export const fetchProposals = () => async (dispatch) => {
  try {
    const { data } = await localhost.get("/nextgen/proposal-kanban");
    if (data) {
      dispatch(setCategories(mapProposals(data.columns)));
      dispatch(setGateMode(data.gateMode));
    }
  } catch (error) {
    dispatch(showErrorModal(error));
  }
};

export const updateProposal =
  (prevColumnId, nextColumnId, proposalId, targetIndex) => async (dispatch) => {
    dispatch(pageLoading(true));
    const params = {
      columnId: nextColumnId,
      position: targetIndex,
    };
    try {
      const { data } = await localhost.put(
        `/nextgen/proposal-kanban/${proposalId}/position`,
        params
      );
      if (data) {
        dispatch(setCategories(mapProposals(data.columns)));
        dispatch(setGateMode(data.gateMode));
      }
    } catch (error) {
      dispatch(showErrorModal(error));
    } finally {
      dispatch(pageLoading(false));
    }
  };

// Skips pageLoading deliberately: the gate move modal stays open (its own
// submitting state disables Confirm) rather than showing the full-page mask.
export const submitProposalMove = createAsyncThunk(
  "dashboard/submitProposalMove",
  async ({ entityId, columnId, position, gate }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await localhost.put(`/nextgen/proposal-kanban/${entityId}/position`, {
        columnId,
        position,
        gate,
      });
      dispatch(setCategories(mapProposals(data.columns)));
      dispatch(setGateMode(data.gateMode));
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteProposal = (proposalId) => async (dispatch) => {
  dispatch(pageLoading(true));
  try {
    await localhost.delete(`/nextgen/proposal/${proposalId}`);
    dispatch(removeProposalById(proposalId));
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(pageLoading(false));
  }
};

export const showDeleteProposalBanner = () => async (dispatch) => {
  dispatch(
    showGlobalAlert({
      variant: alertVariants.success,
      text: "Proposal successfully deleted",
    })
  );
};

export const fetchProposalData = (proposalId) => async (dispatch) => {
  dispatch(pageLoading(true));
  try {
    const url = `/nextgen/proposal/${proposalId}/delete-check`;
    const rsp = await localhost.get(url, undefined);
    return rsp?.data || {};
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(pageLoading(false));
  }
};

export const startProposalAndNavigate =
  (opportunityId, navigate) => async (dispatch) => {
    dispatch(addToJobQueue("startProposalAndNavigate"));
    try {
      const { data: proposalId } = await localhost.post(
        `/nextgen/proposal/${opportunityId}`
      );
      dispatch(hideModal(modalTypes.TRACKED_OPPORTUNITY_PICKER));
      navigate(`/proposals/view/${proposalId}`);
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(removeFromJobQueue("startProposalAndNavigate"));
    }
  };
